/**
 * Calculates the pixels that a particular control hides, and accumulates it in the globalOffset variable.
 * @private
 * @param {google.maps.Map} map 
 * @param {google.maps.ControlPosition} position 
 * @param {object} globalOffset 
 * @param {number} globalOffset.top
 * @param {number} globalOffset.right
 * @param {number} globalOffset.bottom
 * @param {number} globalOffset.left
 */
function calculateControlOffset(map, position, globalOffset) {
  return (control) => {
    let controlOffset = { top: 0, left: 0, right: 0, bottom: 0 };

    //if the component doesn't fill the entire height screen, else ignore it
    if (map.getDiv().offsetHeight !== control.offsetHeight) {
      if (position === google.maps.ControlPosition.TOP_CENTER ||
        position === google.maps.ControlPosition.TOP_LEFT ||
        position === google.maps.ControlPosition.TOP_RIGHT ||
        position === google.maps.ControlPosition.LEFT_TOP ||
        position === google.maps.ControlPosition.RIGHT_TOP) {
        controlOffset.top = control.offsetHeight ? control.offsetTop + control.offsetHeight : control.offsetHeight;
      }

      if (position === google.maps.ControlPosition.BOTTOM_CENTER ||
        position === google.maps.ControlPosition.BOTTOM_LEFT ||
        position === google.maps.ControlPosition.BOTTOM_RIGHT ||
        position === google.maps.ControlPosition.LEFT_BOTTOM ||
        position === google.maps.ControlPosition.RIGHT_BOTTOM) {
        controlOffset.bottom = map.getDiv().offsetHeight - control.offsetTop;
      }
    }
    //if the component doesn't fill the entire width screen, else ignore it
    if (map.getDiv().offsetWidth !== control.offsetWidth) {
      if (position === google.maps.ControlPosition.LEFT_CENTER ||
        position === google.maps.ControlPosition.BOTTOM_LEFT ||
        position === google.maps.ControlPosition.TOP_LEFT ||
        position === google.maps.ControlPosition.LEFT_BOTTOM ||
        position === google.maps.ControlPosition.LEFT_TOP) {
        controlOffset.left = control.offsetWidth ? control.offsetLeft + control.offsetWidth : control.offsetWidth;
      }

      if (position === google.maps.ControlPosition.RIGHT_CENTER ||
        position === google.maps.ControlPosition.BOTTOM_RIGHT ||
        position === google.maps.ControlPosition.TOP_RIGHT ||
        position === google.maps.ControlPosition.RIGHT_BOTTOM ||
        position === google.maps.ControlPosition.RIGHT_TOP) {
        controlOffset.right = map.getDiv().offsetWidth - control.offsetLeft;
      }
    }

    // We only keep the smallest offset, either vertically or horizontally if possible.
    if (controlOffset.top > 0 &&
      (controlOffset.top < controlOffset.left || controlOffset.top < controlOffset.right)) {
      controlOffset.left = 0;
      controlOffset.right = 0;
    } else {
      controlOffset.top = 0;
    }

    if (controlOffset.bottom > 0 &&
      (controlOffset.bottom < controlOffset.left || controlOffset.bottom < controlOffset.right)) {
      controlOffset.left = 0;
      controlOffset.right = 0;
    } else {
      controlOffset.bottom = 0;
    }

    // And then extend the global offset if the new values are bigger.
    if (controlOffset.top > globalOffset.top) {
      globalOffset.top = controlOffset.top;
    }

    if (controlOffset.left > globalOffset.left) {
      globalOffset.left = controlOffset.left;
    }
    if (controlOffset.bottom > globalOffset.bottom) {
      globalOffset.bottom = controlOffset.bottom;
    }
    if (controlOffset.right > globalOffset.right) {
      globalOffset.right = controlOffset.right;
    }
  };
}

/**
 * Returns the offsets that we need to apply in each of the four borders of the map to avoid being hidden
 * by any map control.
 * @private
 * @param {google.maps.Map} map the map instance the offsets are calculated for.
 * @returns {object} The offsets to avoid placing things under for the four borders of the map.
 */
function calculateGlobalOffset(map) {
  let globalOffset = { top: 0, left: 0, right: 0, bottom: 0 };

  for (let position in map.controls) {
    if (!map.controls.hasOwnProperty(position)) {
      continue;
    }
    map.controls[position].forEach(calculateControlOffset(map, parseInt(position), globalOffset));
  }

  return globalOffset;
}

/**
 * Map fitBounds substitute function that applies a bounding box to a given map, preventing
 * the received bounding box from being hidden by a control.
 * @private
 * @param {any} map 
 * @param {any} bbox 
 */
function controlAwareFitBounds(map, bbox) {
  map._oldFitBounds(bbox);

  let projection = map.getProjection();
  if (!projection || !map.getZoom()) {
    // Too early for this kind of operation...
    return;
  }

  let globalOffset = calculateGlobalOffset(map);

  // Now we apply the offsets to the corners of the current bounding bounds.
  let sw = bbox.getSouthWest();
  let ne = bbox.getNorthEast();

  let swPt = projection.fromLatLngToPoint(sw);
  let nePt = projection.fromLatLngToPoint(ne);
  let zoomMultiplier = Math.pow(2, map.getZoom());
  swPt.x -= globalOffset.left / zoomMultiplier;
  swPt.y += globalOffset.bottom / zoomMultiplier;

  nePt.x += globalOffset.right / zoomMultiplier;
  nePt.y -= globalOffset.top / zoomMultiplier;

  sw = projection.fromPointToLatLng(swPt);
  ne = projection.fromPointToLatLng(nePt);

  map._oldFitBounds(new google.maps.LatLngBounds(sw, ne));
}

/**
 * Sets the map center taking into account areas of the map that might be hiden by controls, so the map seems
 * properly centered.
 * @private
 * @param {any} map 
 * @param {any} position 
 */
function controlAwareSetCenter(map, position) {
  let projection = map.getProjection();
  if (!projection || !map.getZoom()) {
    // Too early for this!
    map._oldSetCenter(position);
    return;
  }
  let globalOffset = calculateGlobalOffset(map);

  let point = projection.fromLatLngToPoint(position);

  let zoomMultiplier = 2 * Math.pow(2, map.getZoom());
  // We calculate the camera displacement vector.
  point.x += (globalOffset.right - globalOffset.left) / zoomMultiplier;
  point.y += (globalOffset.bottom - globalOffset.top) / zoomMultiplier;

  map._oldSetCenter(projection.fromPointToLatLng(point));
}

/**
 * Enables centerting and fit bounds avoiding areas hidden by map controls.
 * @memberof core.helpers
 * @param {googe.maps.Map} map 
 */
export default {
  /**
   * Replaces the map vainilla's fitBounds and setCenter functions with
   * substitutes that takes into account the areas hidden by controls in order to properly position
   * the map so nothing that we want to show to the user is hidden.
   */
  init: function(map) {
    map._oldFitBounds = map.fitBounds;
    map.fitBounds = function(bbox) {
      controlAwareFitBounds(map, bbox);
    };

    map._oldSetCenter = map.setCenter;

    function setCenter(position) {
      let stackPiece;
      try {
        throw new Error();
      } catch (error) {
        stackPiece = error.stack ? error.stack.split('\n')[3] : null;
      }
      if ((stackPiece && stackPiece.indexOf('googleapis.com') !== -1) || stackPiece === null) {
        // The code was called from google's script, so we work in vainilla mode to avoid breaking things.
        map._oldSetCenter(position);
        return;
      }
      controlAwareSetCenter(map, position);
    }

    map.setCenter = setCenter;
  }
};



// WEBPACK FOOTER //
// ./lmm-widget/src/helpers/enableControlsAwareCentering.js